import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { terraProxyApiClient } from '../clients/testerClient';
import type { FindingAuditLogsResponse } from '@terra/shared-contracts-findings';

export const FINDING_AUDIT_LOGS_QUERY_KEY = 'query.finding.auditLogs';

export const useFindingAuditLogs = ({
  id,
  skip = 0,
  limit = 50,
  enabled = true,
}: {
  id: string | null;
  skip?: number;
  limit?: number;
  enabled?: boolean;
}) => {
  const queryClient = useQueryClient();

  const result = useQuery({
    queryKey: [FINDING_AUDIT_LOGS_QUERY_KEY, id, skip, limit],
    queryFn: async () => {
      if (!id) {
        throw new Error('Finding ID is required');
      }
      const response = await terraProxyApiClient.getAuditLogs(id, skip, limit);
      if (response?.status === 200) {
        return response.body as FindingAuditLogsResponse;
      }
      return { logs: [], total: 0 };
    },
    enabled: !!id && enabled,
    staleTime: 10000,
    refetchOnWindowFocus: false,
  });

  const invalidate = useCallback(async () => {
    if (!id) return;
    await queryClient.invalidateQueries({
      queryKey: [FINDING_AUDIT_LOGS_QUERY_KEY, id],
      exact: false,
    });
  }, [queryClient, id]);

  return {
    auditLogs: result.data?.logs || [],
    total: result.data?.total || 0,
    isLoading: result.isLoading,
    error: result.error,
    invalidate,
  };
};
